import React, { useState, useEffect, useCallback, useContext, memo, useRef } from 'react';
import {
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonIcon,
  useIonRouter,
  useIonViewWillEnter,
  IonPage,
  IonGrid,
  IonRow,
  IonCol,
  IonButtons,
  IonButton,
  IonItem,
  IonLabel,
  IonText,
  IonList,
  IonModal,
  IonHeader,
  IonToolbar,
  IonBadge,
  IonAvatar,

} from '@ionic/react';
import {
  personOutline,
  peopleOutline,
  trendingUpOutline,
  chatbubblesOutline,
  barChartOutline,
  personAddOutline,
  clipboard,
  cash,
  speedometer,
  pencil,
  notificationsOutline,
  callOutline,
} from 'ionicons/icons';
import { useAuth } from '../../../contexts';
import { AuthContext } from '../../../contexts/auth-context-core';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import { UserInfo as _User } from '@rsmaids/shared/types/auth'
import styles from '../sales/salesDashboard.module.scss';
import { useRemountOnPageHidden } from '../../../hooks/useRemountOnPageHidden';
import { Shortcut } from '@rsmaids/shared/types/dashboard';
import { SALES_SHORTCUTS, SALES_CATEGORY_NAMES } from '../shortcutCatalogues';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { ShortcutsEditModal } from '../../../components/ShortcutsEditModal';
import { fetchLeadsFromApi } from '../../../services/glsaLeadsService';
import type { GlsaLeadDoc } from '../../../services/glsaLeadsService';
import { getBookingsFromCache } from '@rsmaids/shared/services/api/bookingsCacheService';
import type { BookingItem } from '@rsmaids/shared/types/bookings';
import { getServiceOrder } from '../../../services/zohoServiceOrderService';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { intlLocale } from '../../../i18n/locale';

function formatLeadTime(iso: string | null): string {
  if (!iso) return i18n.t('pages.dashboard.sales.DashboardSales.recently');
  const d = new Date(iso);
  if (isNaN(d.getTime())) return i18n.t('pages.dashboard.sales.DashboardSales.recently');
  const ms = Date.now() - d.getTime();
  const mins = Math.floor(ms / 60000);
  const hours = Math.floor(ms / 3600000);
  const days = Math.floor(ms / 86400000);
  if (mins < 2) return i18n.t('pages.dashboard.sales.DashboardSales.justNow');
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return i18n.t('pages.dashboard.sales.DashboardSales.yesterday');
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric' });
}

function leadStatusColor(status: string): string {
  switch (status) {
    case 'NEW': return 'warning';
    case 'ACTIVE': return 'primary';
    case 'BOOKED': return 'success';
    case 'DECLINED': return 'danger';
    default: return 'medium';
  }
}

function leadStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    NEW: i18n.t('pages.dashboard.sales.DashboardSales.new'), ACTIVE: i18n.t('pages.dashboard.sales.DashboardSales.active'), BOOKED: i18n.t('pages.dashboard.sales.DashboardSales.booked'),
    DECLINED: i18n.t('pages.dashboard.sales.DashboardSales.declined'), EXPIRED: i18n.t('pages.dashboard.sales.DashboardSales.expired'), DISABLED: i18n.t('pages.dashboard.sales.DashboardSales.disabled'),
  };
  return labels[status] ?? status;
}

function bookingPipelineColor(status: BookingItem['status']): string {
  switch (status) {
    case 'confirmed': return 'success';
    case 'completed': return 'success';
    case 'cancelled': return 'danger';
    case 'due': return 'warning';
    case 'overdue': return 'danger';
    default: return 'warning';
  }
}

function bookingPipelineLabel(status: BookingItem['status']): string {
  const labels: Record<string, string> = {
    pending: i18n.t('pages.dashboard.sales.DashboardSales.new'), confirmed: i18n.t('pages.dashboard.sales.DashboardSales.active'), completed: i18n.t('pages.dashboard.sales.DashboardSales.done'),
    cancelled: 'Cancelled', due: i18n.t('pages.dashboard.sales.DashboardSales.due'), overdue: i18n.t('pages.dashboard.sales.DashboardSales.overdue'),
  };
  return labels[status] ?? status;
}

function formatPrice(amount: number | undefined): string {
  if (!amount) return '—';
  return amount >= 1000 ? `$${(amount / 1000).toFixed(1)}K` : `$${Math.round(amount)}`;
}

const DashboardSales: React.FC = () => {
  const { t } = useTranslation();
  const router = useIonRouter();
  // Badge = notifications that arrived since the alerts were last opened (from the
  // context's already-loaded newest notifications — no separate fetch or polling).
  const { authState, unreadCount: unreadNotificationsCount } = useAuth();
  const { user } = authState;
  const authContext = useContext(AuthContext);

  const [isEditMode, setIsEditMode] = useState(false);
  const [realLeads, setRealLeads] = useState<GlsaLeadDoc[]>([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(true);
  const [realPipeline, setRealPipeline] = useState<BookingItem[]>([]);
  const [pipelinePriceMap, setPipelinePriceMap] = useState<Record<string, number>>({});

  const [activeShortcutIds, setActiveShortcutIds] = useState<string[]>(
    user?.preferences?.dashboardShortcuts || ['leads', 'analytics', 'profile', 'alerts', 'settings']
  );

  const scrollableContentRef = useRef<HTMLIonGridElement>(null);
  // The scrolling area is rebuilt while the page is hidden, so it is back at the top on return
  const scrollResetKey = useRemountOnPageHidden(scrollableContentRef);

  const goToAlerts = () => {
    router.push('/tabs/dashboard/sales/alerts');
  };





  // Reset scroll position when entering the tab
  useIonViewWillEnter(() => {
    if (scrollableContentRef.current) {
      scrollableContentRef.current.scrollTop = 0;
    }
  });





  // Update shortcuts when user preferences change — computed during render
  // (React's "adjust state when a prop changes" pattern) rather than as a
  // setState call inside an effect.
  const [prevDashboardShortcuts, setPrevDashboardShortcuts] = useState(user?.preferences?.dashboardShortcuts);
  if (prevDashboardShortcuts !== user?.preferences?.dashboardShortcuts) {
    setPrevDashboardShortcuts(user?.preferences?.dashboardShortcuts);
    if (user?.preferences?.dashboardShortcuts) {
      setActiveShortcutIds(user.preferences.dashboardShortcuts);
    }
  }

  useEffect(() => {
    // Deferred via a resolved-promise microtask (not called synchronously
    // in the effect body).
    Promise.resolve().then(() => {
      setIsLoadingLeads(true);
      fetchLeadsFromApi(6).then(leads => {
        setRealLeads(leads.slice(0, 3));
        setIsLoadingLeads(false);
      }).catch(() => setIsLoadingLeads(false));
    });
  }, []);

  useEffect(() => {
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 30);
    getBookingsFromCache(start, end).then(async bookings => {
      const active = bookings
        .filter(b => b.status !== 'cancelled' && b.status !== 'completed')
        .sort((a, b) => {
          const at = a.startTime instanceof Date ? a.startTime.getTime() : new Date(a.startTime).getTime();
          const bt = b.startTime instanceof Date ? b.startTime.getTime() : new Date(b.startTime).getTime();
          return at - bt;
        })
        .slice(0, 3);
      setRealPipeline(active);

      const soResults = await Promise.allSettled(active.map(b => getServiceOrder(b.id)));
      const priceMap: Record<string, number> = {};
      soResults.forEach((result, i) => {
        if (result.status === 'fulfilled' && result.value?.total) {
          priceMap[active[i].id] = result.value.total;
        }
      });
      setPipelinePriceMap(priceMap);
    }).catch(() => {});
  }, []);

  const saveShortcutsToStorage = useCallback(async (shortcuts: string[]) => {
    try {
      // Save to server for cross-device sync
      await DataServiceFactory.getService().auth.updateUserPreferences({
        dashboardShortcuts: shortcuts
      });

      authContext?.actions?.updateLocalPreferences({ dashboardShortcuts: shortcuts });
      localStorage.setItem('dashboard-sales-shortcuts', JSON.stringify(shortcuts));
    } catch (error) {
      console.error('Failed to save shortcuts to server:', error);
      // Fallback to localStorage if server save fails
      localStorage.setItem('dashboard-sales-shortcuts', JSON.stringify(shortcuts));
    }
  }, [authContext]);

  // Computed during render (React's "adjust state when a dependency
  // changes" pattern) rather than as setState calls inside an effect,
  // since this is a pure derivation from user/localStorage with no
  // external system being subscribed to.
  const [prevUserForShortcuts, setPrevUserForShortcuts] = useState(user);
  if (prevUserForShortcuts !== user) {
    setPrevUserForShortcuts(user);
    if (user) {
      // Check if user has saved preferences first
      if (user.preferences?.dashboardShortcuts && user.preferences.dashboardShortcuts.length > 0) {
        setActiveShortcutIds(user.preferences.dashboardShortcuts);
      } else {
        // Check localStorage as secondary fallback
        const localShortcuts = localStorage.getItem('dashboard-sales-shortcuts');
        if (localShortcuts) {
          try {
            const parsedShortcuts = JSON.parse(localShortcuts);
            if (Array.isArray(parsedShortcuts) && parsedShortcuts.length > 0) {
              setActiveShortcutIds(parsedShortcuts);
            } else {
              // Only set defaults if no preferences exist anywhere
              setActiveShortcutIds(['leads', 'analytics', 'profile', 'alerts', 'settings']);
            }
          } catch {
            // Only set defaults if localStorage is corrupted
            setActiveShortcutIds(['leads', 'analytics', 'profile', 'alerts', 'settings']);
          }
        } else {
          // Only set defaults if no localStorage exists
          setActiveShortcutIds(['leads', 'analytics', 'profile', 'alerts', 'settings']);
        }
      }
    }
  }

  const allShortcuts: Shortcut[] = SALES_SHORTCUTS;
  const categoryNames = SALES_CATEGORY_NAMES;

  // Computed values


  const activeShortcuts = activeShortcutIds
    .map(id => allShortcuts.find(s => s.id === id))
    .filter((shortcut): shortcut is Shortcut => shortcut !== undefined);



  return (
    <IonPage className={styles.page}>
      <IonHeader className="ion-no-border">
        <IonToolbar className={styles.dashboardToolbar}>
          <IonButton
            slot="start"
            fill="clear"
            className={styles.avatarButton}
            onClick={() => router.push('/profile')}
            title={t('pages.dashboard.sales.DashboardSales.viewProfile')}
            aria-label={t('pages.dashboard.sales.DashboardSales.viewProfile')}
          >
            <IonAvatar slot="icon-only" className={styles.userAvatar}>
              {user?.avatarUrl ? (
                <img
                  src={proxyImage(user.avatarUrl)}
                  alt={user?.name || ''}
                  onError={(e) => {
                    const imgElement = e.target as HTMLImageElement;
                    imgElement.src = getDefaultAvatar(user?.name || user?.email || 'User');
                  }}
                />
              ) : (
                <img
                  src={getDefaultAvatar(user?.name || user?.email || 'User')}
                  alt={user?.name || t('pages.dashboard.sales.DashboardSales.user')}
                />
              )}
            </IonAvatar>
          </IonButton>
          <IonLabel>
            <h3 className={styles.welcomeText}>{t('pages.dashboard.sales.DashboardSales.welcomeBack')}</h3>
            <h2 className={styles.userName}>
              {user?.firstName || // First try to use firstName if available
                (user?.name ? user.name.split(' ')[0] : t('pages.dashboard.sales.DashboardSales.user')) // Otherwise split the full name
              }
            </h2>
          </IonLabel>
          <IonButtons slot="end">
            <IonButton
              className={styles.headerIconButton}
              onClick={() => router.push('/team/messages')}
              title={t('pages.dashboard.sales.DashboardSales.teamMessaging')}
              aria-label={t('pages.dashboard.sales.DashboardSales.teamMessaging')}
            >
              <IonIcon icon={chatbubblesOutline} slot="icon-only" />
            </IonButton>
            <IonItem lines="none" className={styles.alertsButtonWrapper}>
              <IonButton
                shape='round'
                className={styles.headerIconButton}
                onClick={goToAlerts}
                title={t('pages.dashboard.sales.DashboardSales.alerts')}
                aria-label={t('pages.dashboard.sales.DashboardSales.alerts')}
              >
                <IonIcon icon={notificationsOutline} slot="icon-only" />
              </IonButton>
              {unreadNotificationsCount > 0 && (
                <IonBadge color="danger" className={styles.notificationBadge}>
                  {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                </IonBadge>
              )}
            </IonItem>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent scrollY={false} className={styles.content}>
        <IonGrid className={styles.dashboardContainer}>
          {/* Menu Cards - native horizontal scroll (see .scrollContainer) */}
          <IonRow className={styles.scrollContainer}>
              <IonButton
                fill="clear"
                className={`${styles.menuCard} ${styles.editButton}`}
                onClick={() => setIsEditMode(!isEditMode)}
              >
                <IonLabel className={styles.menuCardContent}>
                  <IonIcon icon={pencil} className={styles.menuIcon} />
                </IonLabel>
              </IonButton>

              <IonButton
                fill="clear"
                className={styles.menuCard}
                onClick={() => window.dispatchEvent(new CustomEvent('rs:openDialer', { detail: {} }))}
              >
                <IonLabel className={styles.menuCardContent}>
                  <IonIcon icon={callOutline} className={styles.menuIcon} />
                  <IonText className={styles.menuTitle}>{t('pages.dashboard.sales.DashboardSales.call')}</IonText>
                </IonLabel>
              </IonButton>

              {activeShortcuts.map((shortcut) => (
                <IonButton
                  key={shortcut.id}
                  fill="clear"
                  className={styles.menuCard}
                  onClick={() => router.push(shortcut.route)}
                >
                  <IonLabel className={styles.menuCardContent}>
                    <IonIcon icon={shortcut.icon} className={styles.menuIcon} />
                    <IonText className={styles.menuTitle}>{shortcut.title}</IonText>
                  </IonLabel>
                </IonButton>
              ))}
            </IonRow>

          {/* Scrollable content with rounded corners */}
          <IonGrid key={scrollResetKey} ref={scrollableContentRef} className={styles.innerScrollContent}>
            {/* Sales Overview Card */}
            <IonCard className={styles.salesOverviewCard}>
              <IonCardHeader>
                <IonCardTitle>{t('pages.dashboard.sales.DashboardSales.salesOverview')}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonGrid>
                  <IonRow>
                    <IonCol size="6" size-md="4">
                      <div className={styles.metricItem}>
                        <div className={styles.metricHeader}>
                          <IonIcon icon={personAddOutline} className={styles.metricIcon} />
                          <IonText className={styles.metricPeriod}>{t('pages.dashboard.sales.DashboardSales.thisWeek')}</IonText>
                        </div>
                        <div className={styles.metricBody}>
                          <IonText className={styles.metricValue}>18</IonText>
                          <IonText className={styles.metricLabel}>{t('pages.dashboard.sales.DashboardSales.newLeads')}</IonText>
                        </div>
                      </div>
                    </IonCol>

                    <IonCol size="6" size-md="4">
                      <div className={styles.metricItem}>
                        <div className={styles.metricHeader}>
                          <IonIcon icon={cash} className={styles.metricIcon} />
                          <IonText className={styles.metricPeriod}>{t('pages.dashboard.sales.DashboardSales.thisMonth')}</IonText>
                        </div>
                        <div className={styles.metricBody}>
                          <IonText className={styles.metricValue}>$12.4K</IonText>
                          <IonText className={styles.metricLabel}>{t('pages.dashboard.sales.DashboardSales.revenue')}</IonText>
                        </div>
                      </div>
                    </IonCol>

                    <IonCol size="6" size-md="4">
                      <div className={styles.metricItem}>
                        <div className={styles.metricHeader}>
                          <IonIcon icon={barChartOutline} className={styles.metricIcon} />
                          <IonText className={styles.metricPeriod}>{t('pages.dashboard.sales.DashboardSales.thisMonth')}</IonText>
                        </div>
                        <div className={styles.metricBody}>
                          <IonText className={styles.metricValue}>24%</IonText>
                          <IonText className={styles.metricLabel}>{t('pages.dashboard.sales.DashboardSales.conversion')}</IonText>
                        </div>
                      </div>
                    </IonCol>

                    <IonCol size="6" size-md="4">
                      <div className={styles.metricItem}>
                        <div className={styles.metricHeader}>
                          <IonIcon icon={trendingUpOutline} className={styles.metricIcon} />
                          <IonText className={styles.metricPeriod}>{t('pages.dashboard.sales.DashboardSales.perSale')}</IonText>
                        </div>
                        <div className={styles.metricBody}>
                          <IonText className={styles.metricValue}>$687</IonText>
                          <IonText className={styles.metricLabel}>{t('pages.dashboard.sales.DashboardSales.avgDeal')}</IonText>
                        </div>
                      </div>
                    </IonCol>

                    <IonCol size="6" size-md="4">
                      <div className={styles.metricItem}>
                        <div className={styles.metricHeader}>
                          <IonIcon icon={peopleOutline} className={styles.metricIcon} />
                          <IonText className={styles.metricPeriod}>{t('pages.dashboard.sales.DashboardSales.totalValue')}</IonText>
                        </div>
                        <div className={styles.metricBody}>
                          <IonText className={styles.metricValue}>$45.2K</IonText>
                          <IonText className={styles.metricLabel}>{t('pages.dashboard.sales.DashboardSales.pipeline')}</IonText>
                        </div>
                      </div>
                    </IonCol>

                    <IonCol size="6" size-md="4">
                      <div className={styles.metricItem}>
                        <div className={styles.metricHeader}>
                          <IonIcon icon={speedometer} className={styles.metricIcon} />
                          <IonText className={styles.metricPeriod}>{t('pages.dashboard.sales.DashboardSales.thisQuarter')}</IonText>
                        </div>
                        <div className={styles.metricBody}>
                          <IonText className={styles.metricValue}>68%</IonText>
                          <IonText className={styles.metricLabel}>{t('pages.dashboard.sales.DashboardSales.winRate')}</IonText>
                        </div>
                      </div>
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonCardContent>
            </IonCard>

            {/* Recent Leads Card */}
            <IonCard className={styles.welcomeCard}>
              <IonCardHeader>
                <IonCardTitle className={styles.welcomeTitle}>{t('pages.dashboard.sales.DashboardSales.recentLeads')}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent className={styles.welcomeContent}>
                <IonList lines="none">
                  {isLoadingLeads ? (
                    <IonItem>
                      <IonLabel><p>{t('pages.dashboard.sales.DashboardSales.loadingLeads')}</p></IonLabel>
                    </IonItem>
                  ) : realLeads.length === 0 ? (
                    <IonItem>
                      <IonLabel><p>{t('pages.dashboard.sales.DashboardSales.noRecentLeads')}</p></IonLabel>
                    </IonItem>
                  ) : realLeads.map(lead => (
                    <IonItem key={lead.id} button detail onClick={() => router.push('/leads')}>
                      <IonIcon icon={personOutline} slot="start" color="primary" />
                      <IonLabel>
                        <h3>{lead.contactName || t('pages.dashboard.sales.DashboardSales.unknownLead')}</h3>
                        <p>{lead.leadType === 'PHONE_CALL' ? t('pages.dashboard.sales.DashboardSales.phoneCall') : t('pages.dashboard.sales.DashboardSales.message')} - {formatLeadTime(lead.createdAt)}</p>
                      </IonLabel>
                      <IonBadge slot="end" color={leadStatusColor(lead.status)}>{leadStatusLabel(lead.status)}</IonBadge>
                    </IonItem>
                  ))}
                  <IonItem button detail onClick={() => router.push('/leads')}>
                    <IonIcon icon={personOutline} slot="start" color="primary" />
                    <IonLabel>
                      <h3>{t('pages.dashboard.sales.DashboardSales.viewAllLeads')}</h3>
                      <p>{t('pages.dashboard.sales.DashboardSales.manageAllYourLeads')}</p>
                    </IonLabel>
                    <IonIcon icon={barChartOutline} slot="end" color="primary" />
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>

            {/* Sales Pipeline Card */}
            <IonCard className={styles.welcomeCard}>
              <IonCardHeader>
                <IonCardTitle className={styles.welcomeTitle}>{t('pages.dashboard.sales.DashboardSales.salesPipeline')}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent className={styles.welcomeContent}>
                <IonList lines="none">
                  {realPipeline.length === 0 ? (
                    <IonItem>
                      <IonLabel><p>{t('pages.dashboard.sales.DashboardSales.noActiveBookingsInPipeline')}</p></IonLabel>
                    </IonItem>
                  ) : realPipeline.map(booking => {
                    const color = bookingPipelineColor(booking.status);
                    const nameParts = (booking.contact?.name || 'Client').trim().split(/\s+/);
                    const shortName = nameParts.length > 1
                      ? `${nameParts[0]} ${nameParts[nameParts.length - 1][0]}.`
                      : nameParts[0];
                    return (
                      <IonItem key={booking.id} button detail onClick={() => router.push('/bookings')}>
                        <IonIcon icon={clipboard} slot="start" color={color} />
                        <IonLabel>
                          <h3>{booking.serviceType || booking.title} - {shortName}</h3>
                          <p>{formatPrice(pipelinePriceMap[booking.id])} - {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}</p>
                        </IonLabel>
                        <IonBadge slot="end" color={color}>{bookingPipelineLabel(booking.status)}</IonBadge>
                      </IonItem>
                    );
                  })}
                  <IonItem button detail onClick={() => router.push('/bookings')}>
                    <IonIcon icon={barChartOutline} slot="start" color="primary" />
                    <IonLabel>
                      <h3>{t('pages.dashboard.sales.DashboardSales.viewFullPipeline')}</h3>
                      <p>{t('pages.dashboard.sales.DashboardSales.allUpcomingBookings')}</p>
                    </IonLabel>
                    <IonIcon icon={trendingUpOutline} slot="end" color="success" />
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>

            {/* Modal (stays outside the scrollable area) */}
            <IonModal
              isOpen={isEditMode}
              onDidDismiss={() => setIsEditMode(false)}
              className={styles.editModal}
            >
              <ShortcutsEditModal
                onDismiss={() => setIsEditMode(false)}
                activeShortcutIds={activeShortcutIds}
                allShortcuts={allShortcuts}
                categoryNames={categoryNames}
                onSave={async (shortcuts) => {
                  setActiveShortcutIds(shortcuts);
                  await saveShortcutsToStorage(shortcuts);
                }}
              />
            </IonModal>
          </IonGrid>
        </IonGrid>
      </IonContent>

    </IonPage>
  );
};

export default memo(DashboardSales);